import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  AppBar,
  Toolbar,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { ArrowForward, ArrowBack } from '@mui/icons-material';
import PaymentForm from '../components/payment/PaymentForm';
import PaymentStatus from '../components/payment/PaymentStatus';
import Logo from '../components/common/Logo';
import CurrencySelect from '../components/common/CurrencySelect';
import GoogleReviewBadge from '../components/common/GoogleReviewBadge';
import { CustomerInfoFormData, PaymentFormData, PaymentLink, Currency, CreatePaymentLinkInput } from '../types/PaymentLink';
import { validateEmail, validatePhoneSimple, validateAmount } from '../utils/validators';
import { createGuestPaymentLink, createPaymentAttempt, updatePaymentStatus } from '../services/paymentLinkService';
import { processPayment } from '../services/bankService';
import { PAYMENT_CONFIG } from '../config/payment';
import { toast } from 'react-toastify';
import { getTodayForDateInput } from '../utils/dateUtils';

const getDefaultDueDates = (): Pick<CustomerInfoFormData, 'depositDueDate' | 'invoiceDueDate'> => {
  const today = getTodayForDateInput();
  return {
    depositDueDate: new Date(today),
    invoiceDueDate: today,
  };
};

interface FormErrors {
  payerFirstName?: string;
  payerLastName?: string;
  customerEmail?: string;
  customerPhone?: string;
  invoiceAmount?: string;
  depositAmount?: string;
}

const GuestPaymentPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfoFormData>({
    payerFirstName: '',
    payerLastName: '',
    customerEmail: '',
    customerPhone: '',
    invoiceNumber: '',
    invoiceAmount: 0,
    depositAmount: 0,
    currency: 'EUR',
    description: '',
    ...getDefaultDueDates(),
    allowPartialPayment: false, // Guest payments default to full payment
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [paymentLink, setPaymentLink] = useState<PaymentLink | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'failed'>(
    'idle'
  );
  const [errorMessage, setErrorMessage] = useState<string>('');

  const steps = ['Your Information', 'Payment Details'];

  const handleChange = (field: keyof CustomerInfoFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let updates: Partial<CustomerInfoFormData> = {};

    if (field === 'invoiceAmount') {
      const amount = parseFloat(event.target.value) || 0;
      updates = {
        invoiceAmount: amount,
        depositAmount: amount, // For guest payments, require full amount
      };
    } else {
      const value = event.target.value;
      updates = { [field]: value };
    }

    setCustomerInfo({
      ...customerInfo,
      ...updates,
    });

    if (errors[field as keyof FormErrors]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };

  const handleCurrencyChange = (currency: Currency) => {
    setCustomerInfo({
      ...customerInfo,
      currency,
    });
  };

  const handleCheckboxChange = (field: keyof CustomerInfoFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCustomerInfo({
      ...customerInfo,
      [field]: event.target.checked,
    });
  };

  const validateStep1 = (): boolean => {
    const newErrors: FormErrors = {};

    if (!customerInfo.payerFirstName.trim()) {
      newErrors.payerFirstName = 'First name is required';
    } else if (customerInfo.payerFirstName.trim().length < 2) {
      newErrors.payerFirstName = 'First name must be at least 2 characters';
    }

    if (!customerInfo.payerLastName.trim()) {
      newErrors.payerLastName = 'Last name is required';
    } else if (customerInfo.payerLastName.trim().length < 2) {
      newErrors.payerLastName = 'Last name must be at least 2 characters';
    }

    if (!customerInfo.customerEmail.trim()) {
      newErrors.customerEmail = 'Email is required';
    } else if (!validateEmail(customerInfo.customerEmail)) {
      newErrors.customerEmail = 'Invalid email format';
    }

    if (customerInfo.customerPhone.trim() && !validatePhoneSimple(customerInfo.customerPhone)) {
      newErrors.customerPhone = 'Please enter a valid phone number (at least 10 digits)';
    }

    if (customerInfo.invoiceAmount <= 0 || !validateAmount(customerInfo.invoiceAmount)) {
      newErrors.invoiceAmount = 'Amount must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      if (!validateStep1()) {
        return;
      }

      setLoading(true);
      try {
        // Create temporary payment link
        // Prepare input data, only including optional fields if they have values
        const inputData: CreatePaymentLinkInput = {
          customerName: `${customerInfo.payerFirstName} ${customerInfo.payerLastName}`,
          customerEmail: customerInfo.customerEmail.trim(),
          customerPhone: customerInfo.customerPhone?.trim() || '',
          invoiceAmount: customerInfo.invoiceAmount,
          depositAmount: customerInfo.depositAmount,
          currency: customerInfo.currency,
          description: customerInfo.description.trim() || 'Guest Payment',
          depositDueDate: customerInfo.depositDueDate,
          invoiceDueDate: customerInfo.invoiceDueDate,
          allowPartialPayment: customerInfo.allowPartialPayment,
          createdBy: 'guest',
          type: 'guest' as const,
          payerFirstName: customerInfo.payerFirstName.trim(),
          payerLastName: customerInfo.payerLastName.trim(),
        };

        // Only include invoice number if it has a value
        const invoiceNum = customerInfo.invoiceNumber?.trim();
        if (invoiceNum) {
          inputData.invoiceNumber = invoiceNum;
        }

        // Debug logging
        console.log('GuestPaymentPage - Creating payment link:', {
          allowPartialPayment: inputData.allowPartialPayment,
          invoiceAmount: inputData.invoiceAmount,
          depositAmount: inputData.depositAmount
        });

        const link = await createGuestPaymentLink(inputData);

        // Debug logging
        console.log('GuestPaymentPage - Payment link created:', {
          linkId: link.linkId,
          allowPartialPayment: link.allowPartialPayment,
          invoiceAmount: link.invoiceAmount,
          depositAmount: link.depositAmount
        });

        setPaymentLink(link);
        setActiveStep(1);
        toast.success('Please proceed with payment');
      } catch (error) {
        console.error('Error creating payment link:', error);
        toast.error('Failed to create payment session. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handlePaymentSubmit = async (paymentData: PaymentFormData) => {
    if (!paymentLink) return;

    if (PAYMENT_CONFIG.useRealPayment) {
      console.warn('[GuestPaymentPage] handlePaymentSubmit called while real payments enabled.');
      toast.info('Redirecting to bank for secure payment. Please follow the 3D Secure flow.');
      return;
    }

    try {
      setPaymentStatus('processing');
      setErrorMessage('');

      await updatePaymentStatus(paymentLink.id, 'processing');

      const result = await processPayment(paymentData, paymentLink.invoiceAmount);

      if (result.success) {
        await createPaymentAttempt(
          paymentLink.linkId,
          paymentLink.invoiceAmount,
          'success',
          result.cardLastFour,
          result.cardType
        );

        await updatePaymentStatus(paymentLink.id, 'success');

        setPaymentStatus('success');
        toast.success('Payment successful!');
      } else {
        await createPaymentAttempt(
          paymentLink.linkId,
          paymentLink.invoiceAmount,
          'failed',
          result.cardLastFour,
          result.cardType,
          result.errorCode,
          result.errorMessage
        );

        await updatePaymentStatus(paymentLink.id, 'failed');

        setPaymentStatus('failed');
        setErrorMessage(result.errorMessage || 'Payment failed');
        toast.error(result.errorMessage || 'Payment failed');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setPaymentStatus('failed');
      setErrorMessage('An unexpected error occurred. Please try again.');
      toast.error('An unexpected error occurred');
    }
  };

  const handleRetry = () => {
    setPaymentStatus('idle');
    setErrorMessage('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
        backgroundImage:
          'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%232e7d32" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        pb: 4,
      }}
    >
      {/* Header */}
      <AppBar
        position="static"
        elevation={2}
        sx={{
          background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
        }}
      >
        <Toolbar sx={{ justifyContent: 'center' }}>
          <Box
            sx={{
              backgroundColor: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              display: 'inline-flex',
            }}
          >
            <Logo size="medium" />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ mt: 4 }}>
        {paymentStatus === 'idle' ? (
          <Card sx={{ boxShadow: 4 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              {/* Stepper */}
              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Step 1: Customer Information */}
              {activeStep === 0 && (
                  <Box>
                    <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 700, mb: 3 }}>
                      Enter Your Information
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="First Name"
                          value={customerInfo.payerFirstName}
                          onChange={handleChange('payerFirstName')}
                          error={!!errors.payerFirstName}
                          helperText={errors.payerFirstName}
                          required
                          disabled={loading}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Last Name"
                          value={customerInfo.payerLastName}
                          onChange={handleChange('payerLastName')}
                          error={!!errors.payerLastName}
                          helperText={errors.payerLastName}
                          required
                          disabled={loading}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          type="email"
                          value={customerInfo.customerEmail}
                          onChange={handleChange('customerEmail')}
                          error={!!errors.customerEmail}
                          helperText={errors.customerEmail}
                          required
                          disabled={loading}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone Number (Optional)"
                          value={customerInfo.customerPhone}
                          onChange={handleChange('customerPhone')}
                          error={!!errors.customerPhone}
                          helperText={errors.customerPhone || 'Optional - Enter your phone number in any format'}
                          disabled={loading}
                          placeholder="e.g., +90 555 123 4567 or 0555 123 4567"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Invoice Number (Optional)"
                          value={customerInfo.invoiceNumber}
                          onChange={handleChange('invoiceNumber')}
                          disabled={loading}
                          placeholder="e.g., INV-2025-001"
                          helperText="Optional - Enter if you have an invoice number"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Amount"
                          type="number"
                          value={customerInfo.invoiceAmount || ''}
                          onChange={handleChange('invoiceAmount')}
                          error={!!errors.invoiceAmount}
                          helperText={errors.invoiceAmount}
                          required
                          disabled={loading}
                          inputProps={{
                            min: 1,
                            step: 0.01,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <CurrencySelect
                          value={customerInfo.currency}
                          onChange={handleCurrencyChange}
                          disabled={loading}
                          required
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Description (Optional)"
                          value={customerInfo.description}
                          onChange={handleChange('description')}
                          disabled={loading}
                          multiline
                          rows={2}
                          placeholder="Golf reservation, Green fee, etc."
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={customerInfo.allowPartialPayment}
                              onChange={handleCheckboxChange('allowPartialPayment')}
                              disabled={loading}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">Allow Partial Payments</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Enable this to pay in multiple installments instead of full amount
                              </Typography>
                            </Box>
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          size="large"
                          fullWidth
                          onClick={handleNext}
                          disabled={loading}
                          endIcon={loading ? <CircularProgress size={20} /> : <ArrowForward />}
                          sx={{
                            py: 2,
                            background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
                          }}
                        >
                          {loading ? 'Processing...' : 'Continue to Payment'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
              )}

              {/* Step 2: Payment Form */}
              {activeStep === 1 && paymentLink && (
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Payment Summary
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Name: {customerInfo.payerFirstName} {customerInfo.payerLastName}
                    </Typography>
                    {customerInfo.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        Note: {customerInfo.description}
                      </Typography>
                    )}
                    <Typography variant="h5" color="primary" sx={{ fontWeight: 700, mt: 1 }}>
                      Amount: {customerInfo.invoiceAmount.toLocaleString('en-US', { style: 'currency', currency: customerInfo.currency })}
                    </Typography>
                  </Box>

                  <PaymentForm
                    amount={paymentLink.invoiceAmount}
                    onSubmit={handlePaymentSubmit}
                    disabled={paymentStatus !== 'idle'}
                    allowPartialPayment={paymentLink.allowPartialPayment}
                    depositAmount={paymentLink.depositAmount}
                    depositPaid={paymentLink.depositPaid}
                    remainingAmount={paymentLink.remainingAmount}
                    currency={paymentLink.currency}
                    useRealPayment={PAYMENT_CONFIG.useRealPayment}
                    paymentLinkId={paymentLink.id}
                    linkId={paymentLink.linkId}
                  />

                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={handleBack}
                    disabled={paymentStatus !== 'idle'}
                    startIcon={<ArrowBack />}
                    sx={{ mt: 2 }}
                  >
                    Back to Information
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        ) : (
          <PaymentStatus
            status={paymentStatus}
            amount={paymentStatus === 'success' ? customerInfo.invoiceAmount : undefined}
            currency={paymentStatus === 'success' ? customerInfo.currency : undefined}
            errorMessage={errorMessage}
            onRetry={paymentStatus === 'failed' ? handleRetry : undefined}
          />
        )}
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 4,
          textAlign: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        {/* Google Review Badge */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <GoogleReviewBadge variant="compact" />
        </Box>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          © {new Date().getFullYear()} Alexa Travel Agency. All rights reserved.
        </Typography>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <Box
            component="img"
            src="/assets/visit-antalya-2.svg"
            alt="Visit Antalya"
            sx={{
              maxWidth: { xs: 150, sm: 200 },
              height: 'auto',
              opacity: 0.8,
            }}
            onError={(e: any) => {
              // Hide image if not found
              e.target.style.display = 'none';
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default GuestPaymentPage;
